/**
 * Publication (`POST /admin/content/publish`).
 *
 * C'est l'écran qui matérialise A-30 et §8.5 : un brouillon est édité, une publication crée une
 * version, change le hash servi et **clôt les runs en cours**. Trois conséquences pour l'interface :
 *
 * 1. le bouton ouvre une **confirmation explicite** qui énumère les conséquences — pas un
 *    « êtes-vous sûr ? » ;
 * 2. le hash **avant** (manifeste publié) et **après** (réponse de publication) sont affichés
 *    côte à côte, avec l'horodatage serveur ;
 * 3. le double-clic est neutralisé : le dialogue verrouille son bouton et `publier()` sort
 *    immédiatement si un appel est déjà en cours, donc **un seul** `POST` part.
 *
 * Le hash « avant » vient de `GET /content/manifest` : aucune route d'administration n'expose la
 * version publiée (constat remonté au propriétaire de l'API, voir LIVRABLE-M3-09.md §9).
 */
import { useRef, useState } from 'react';

import { listerAudit, lireVersionPubliee, publierContenu } from '../api/admin';
import { ErreurApi, versErreurApi } from '../api/erreurs';
import { M } from '../catalogue/messages';
import { useChargement } from '../hooks/use-chargement';
import { Bandeau, BandeauErreur } from '../ui/Bandeau';
import { Bouton } from '../ui/Bouton';
import { Chargement } from '../ui/Chargement';
import { Coquille } from '../ui/Coquille';
import { DialogueConfirmation } from '../ui/DialogueConfirmation';
import { Panneau } from '../ui/Panneau';
import { formaterDate, hashCourt } from '../utils/presentation';

/** État de l'écran au chargement : version publiée et présence d'un brouillon. */
interface EtatPublication {
  readonly hashPublie: string | null;
  /** `true` si la dernière écriture du journal précède une publication. */
  readonly brouillonEnAttente: boolean | null;
  readonly manifesteIllisible: boolean;
}

/** Résultat d'une publication : les deux hash, pour la comparaison demandée. */
interface ResultatPublication {
  readonly hashAvant: string | null;
  readonly hashApres: string;
  readonly publieLe: string;
}

async function chargerEtat(signal: AbortSignal): Promise<EtatPublication> {
  const [manifeste, audit] = await Promise.all([
    lireVersionPubliee(signal).catch((inconnu: unknown) => {
      // Le manifeste peut échouer (droits, contenu absent) : l'écran reste utilisable, mais
      // l'absence de version « avant » est signalée explicitement.
      if (inconnu instanceof ErreurApi) return null;
      throw inconnu;
    }),
    listerAudit({ limit: 1, offset: 0 }, signal),
  ]);
  const dernier = audit.logs[0];
  return {
    hashPublie: manifeste === null ? null : manifeste.hash,
    brouillonEnAttente: dernier === undefined ? false : dernier.action !== 'content.publish',
    manifesteIllisible: manifeste === null,
  };
}

export function EcranPublication() {
  const chargement = useChargement(chargerEtat);
  const [dialogue, setDialogue] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const [refus, setRefus] = useState<ErreurApi | null>(null);
  const [resultat, setResultat] = useState<ResultatPublication | null>(null);

  /**
   * Verrou **synchrone** de publication.
   *
   * `enCours` (état React) ne suffit pas : deux clics dans la même tâche lisent la même valeur
   * fermée et produiraient deux `POST` avant le premier rendu. Une référence est partagée
   * immédiatement, donc le deuxième appel sort avant tout réseau. Le test
   * `publication.spec.tsx` vérifie ce cas exact (trois clics, un seul appel).
   */
  const verrou = useRef(false);

  const etat = chargement.donnees;

  /**
   * Publie le brouillon — **une seule fois**.
   *
   * Le verrou d'entrée (`enCours`) est posé avant l'appel réseau : deux clics dans la même frame
   * ne peuvent pas produire deux `POST`, même si React n'a pas encore re-rendu le bouton.
   */
  async function publier(): Promise<void> {
    if (verrou.current) return;
    verrou.current = true;
    setEnCours(true);
    setRefus(null);
    try {
      const hashAvant = etat?.hashPublie ?? null;
      const reponse = await publierContenu(hashAvant === null ? undefined : hashAvant);
      setResultat({
        hashAvant,
        hashApres: reponse.contentVersion,
        publieLe: reponse.publishedAt,
      });
      setDialogue(false);
      chargement.recharger();
    } catch (inconnu) {
      setRefus(versErreurApi(inconnu));
      setDialogue(false);
    } finally {
      verrou.current = false;
      setEnCours(false);
    }
  }

  const refusDetaille = (): string => {
    if (refus === null) return M.publication.echecValidation;
    const details = refus.details ?? {};
    if (refus.code === 'VALIDATION_FAILED' && ('attendu' in details || 'recu' in details)) {
      return M.publication.echecVersion;
    }
    return M.publication.echecValidation;
  };

  return (
    <Coquille
      titre={M.publication.titre}
      sousTitre={M.publication.sousTitre}
      actions={
        <Bouton variante="secondaire" onClick={chargement.recharger}>
          {M.commun.actualiser}
        </Bouton>
      }
    >
      <Panneau>
        {chargement.erreur === null ? null : (
          <BandeauErreur erreur={chargement.erreur} surReessai={chargement.recharger} />
        )}
        {chargement.chargement ? <Chargement /> : null}

        {etat === null ? null : (
          <>
            <dl className="fiche">
              <dt>{M.publication.versionPubliee}</dt>
              <dd>
                {etat.hashPublie === null ? (
                  <span className="texte-avertissement">{M.publication.versionInconnue}</span>
                ) : (
                  <code title={etat.hashPublie}>{etat.hashPublie}</code>
                )}
              </dd>
              <dt>{M.publication.etatBrouillon}</dt>
              <dd>
                {etat.brouillonEnAttente === null
                  ? M.publication.brouillonIndetermine
                  : etat.brouillonEnAttente
                    ? M.publication.brouillonOui
                    : M.publication.brouillonNon}
              </dd>
            </dl>

            {refus === null ? null : <BandeauErreur erreur={refus} titre={refusDetaille()} />}

            {resultat === null ? null : (
              <div className="resultat-publication" role="status">
                <h2 className="panneau__titre">{M.publication.publieTitre}</h2>
                <dl className="fiche">
                  <dt>{M.publication.hashAvant}</dt>
                  <dd>
                    {resultat.hashAvant === null ? (
                      M.commun.vide
                    ) : (
                      <code title={resultat.hashAvant}>{hashCourt(resultat.hashAvant)}</code>
                    )}
                  </dd>
                  <dt>{M.publication.hashApres}</dt>
                  <dd>
                    <code title={resultat.hashApres}>{hashCourt(resultat.hashApres)}</code>
                  </dd>
                  <dt>{M.publication.publieLeLabel}</dt>
                  <dd>{formaterDate(resultat.publieLe)}</dd>
                </dl>
                {resultat.hashAvant !== null && resultat.hashAvant === resultat.hashApres ? (
                  <Bandeau texte={M.publication.hashInchange} ton="info" />
                ) : null}
              </div>
            )}

            <div className="formulaire__actions">
              <Bouton
                variante="danger"
                onClick={() => {
                  setDialogue(true);
                }}
              >
                {M.publication.bouton}
              </Bouton>
            </div>
            <p className="formulaire__note">{M.publication.gardeOptimiste}</p>
          </>
        )}
      </Panneau>

      <DialogueConfirmation
        ouvert={dialogue}
        titre={M.publication.confirmationTitre}
        corps={M.publication.confirmationCorps}
        consequences={M.publication.consequences}
        libelleConfirmer={M.publication.confirmationValider}
        libelleAnnuler={M.publication.confirmationAnnuler}
        enCours={enCours}
        surConfirmer={() => {
          void publier();
        }}
        surAnnuler={() => {
          setDialogue(false);
        }}
      />
    </Coquille>
  );
}
